/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { Info } from 'lucide-react';
import { useState, type CSSProperties, type ReactNode } from 'react';
import CodeBlock from '@/components/code/CodeBlock';
import AppLoader from '@/components/feedback/AppLoader';
import SensorRequestMessage from '@/components/feedback/SensorRequestMessage';
import { useI18n } from '@/context/I18nContext';
import { useTechnicalDetails } from '@/context/TechnicalDetailsContext';
import { formatKpiDateTime } from '@/lib/formatting/dateTime';
import {
    getStatusClassName,
    getStatusSeverityFromTrafficLight,
} from '@/lib/overview/overviewCards';
import {
    formatTimeSeriesNumericValue,
    resolveTimeSeriesTrafficLightLegendCondition,
    resolveTimeSeriesTrafficLightLabel,
    resolveTimeSeriesTrafficLightSeverity,
    resolveTimeSeriesTrafficLightTitle,
    resolveTimeSeriesValueLabel,
    type TimeSeriesTrafficLightConfig,
    type TimeSeriesTrafficLightSeverity,
    type TimeSeriesValueLabelsConfig,
} from '@/lib/timeSeries/timeSeriesValue';

interface TimeSeriesValueCardModalConfig {
    buttonLabel?: string;
    message: string;
    title?: string;
}

interface TimeSeriesValueCardProps {
    conversion?: number;
    displayValue?: string | null;
    errorMessage?: string;
    fractionDigits?: number;
    info?: string;
    isLoading?: boolean;
    loadingLabel?: string;
    link?: string;
    legendConversion?: number;
    legendCode?: string;
    legendCodeMissingMessage?: string;
    legendFractionDigits?: number;
    legendNote?: string;
    legendOffset?: number;
    legendDisplaySensor?: string;
    legendSensor?: string;
    legendTrafficLight?: TimeSeriesTrafficLightConfig;
    legendTrafficLightValue?: number | null;
    legendUnit?: string | null;
    modal?: TimeSeriesValueCardModalConfig;
    metaText?: string | null;
    metaDate?: string | null;
    offset?: number;
    resolveUnit?: (value: number) => string | null;
    showLegend?: boolean;
    title?: string;
    trafficLight?: TimeSeriesTrafficLightConfig;
    trafficLightValue?: number | null;
    trimFractionDigitsForIntegers?: boolean;
    transformValue?: (value: number) => number;
    unit?: string | null;
    value: number | null;
    valueLabels?: TimeSeriesValueLabelsConfig;
}

type TimeSeriesValueCardStyle = CSSProperties & {
    '--status-background'?: string;
    '--time-series-kpi-danger'?: string;
    '--time-series-kpi-implausible'?: string;
    '--time-series-kpi-success'?: string;
    '--time-series-kpi-warning'?: string;
};

function getTrafficLightSeverityLabel(
    severity: TimeSeriesTrafficLightSeverity,
    trafficLight: TimeSeriesTrafficLightConfig | undefined,
    t: (key: string, values?: Record<string, string>) => string,
): string {
    let defaultLabel: string;

    switch (severity) {
        case 'danger':
            defaultLabel = t('Error');
            break;
        case 'warning':
            defaultLabel = t('Warning');
            break;
        case 'implausible':
            defaultLabel = t('Implausible');
            break;
        case 'success':
        default:
            defaultLabel = t('OK');
    }

    return resolveTimeSeriesTrafficLightLabel(severity, trafficLight, defaultLabel);
}

function getTrafficLightOperatorSymbol(operator: 'lt' | 'lte' | 'gt' | 'gte'): string {
    switch (operator) {
        case 'lt':
            return '<';
        case 'lte':
            return '<=';
        case 'gt':
            return '>';
        case 'gte':
        default:
            return '>=';
    }
}

function formatTrafficLightLegendThreshold(
    value: number,
    locale: string,
    conversion: number,
    fractionDigits?: number,
    offset = 0,
    unit?: string | null,
): string {
    const convertedValue = value * conversion + offset;
    const formattedValue = formatTimeSeriesNumericValue(
        convertedValue,
        locale,
        fractionDigits,
    );

    return unit ? `${formattedValue} ${unit}` : formattedValue;
}

function TimeSeriesValueCard({
    conversion = 1,
    displayValue = null,
    errorMessage = '',
    fractionDigits,
    info,
    isLoading = false,
    loadingLabel,
    link,
    legendConversion,
    legendCode,
    legendCodeMissingMessage,
    legendFractionDigits,
    legendNote,
    legendOffset,
    legendDisplaySensor,
    legendSensor,
    legendTrafficLight,
    legendTrafficLightValue,
    legendUnit,
    modal,
    metaText,
    metaDate,
    offset = 0,
    resolveUnit,
    showLegend = false,
    title,
    trafficLight,
    trafficLightValue,
    trimFractionDigitsForIntegers = false,
    transformValue,
    unit,
    value,
    valueLabels,
}: TimeSeriesValueCardProps) {
    const { locale, t } = useI18n();
    const showTechnicalDetails = useTechnicalDetails();
    const codeBlockTheme = document.documentElement.getAttribute('data-theme') === 'dark'
        ? 'dark'
        : 'light';
    const [isCardModalOpen, setIsCardModalOpen] = useState(false);
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const [isLegendModalOpen, setIsLegendModalOpen] = useState(false);
    const convertedValue = value === null ? null : value * conversion + offset;
    const transformedValue = convertedValue === null
        ? null
        : (transformValue?.(convertedValue) ?? convertedValue);
    const resolvedUnit = convertedValue === null || resolveUnit === undefined
        ? unit
        : resolveUnit(convertedValue);
    const resolvedLegendConversion = legendConversion ?? conversion;
    const resolvedLegendFractionDigits = legendFractionDigits ?? fractionDigits;
    const resolvedLegendOffset = legendOffset ?? offset;
    const resolvedLegendUnit = legendUnit !== undefined ? legendUnit : unit;
    const resolvedTrafficLightValue = trafficLightValue === undefined
        ? value
        : trafficLightValue;
    const resolvedLegendTrafficLight = legendTrafficLight ?? trafficLight;
    const resolvedLegendTrafficLightValue = legendTrafficLightValue === undefined
        ? resolvedTrafficLightValue
        : legendTrafficLightValue;
    const trafficLightSeverity = resolveTimeSeriesTrafficLightSeverity(
        resolvedTrafficLightValue,
        trafficLight,
    );
    const legendTrafficLightSeverity = resolveTimeSeriesTrafficLightSeverity(
        resolvedLegendTrafficLightValue,
        resolvedLegendTrafficLight,
    );
    const resolvedTitle =
        resolveTimeSeriesTrafficLightTitle(resolvedTrafficLightValue, trafficLight) ?? title;
    const legendSeverities = resolvedLegendTrafficLight
        ? Array.from(
            new Set([
                ...resolvedLegendTrafficLight.rules.map((rule) => rule.severity),
                ...(resolvedLegendTrafficLight.fallback ? [resolvedLegendTrafficLight.fallback] : []),
            ]),
        )
        : [];
    const legendTitles = new Map(
        legendSeverities.map((severity) => {
            const severityLabel = getTrafficLightSeverityLabel(severity, resolvedLegendTrafficLight, t);
            const legendCondition = resolveTimeSeriesTrafficLightLegendCondition(severity, resolvedLegendTrafficLight);

            if (!legendCondition) {
                return [severity, severityLabel] as const;
            }

            return [
                severity,
                `${severityLabel}: ${t('Value')} ${getTrafficLightOperatorSymbol(legendCondition.operator)} ${
                    formatTrafficLightLegendThreshold(
                        legendCondition.value,
                        locale,
                        resolvedLegendConversion,
                        resolvedLegendFractionDigits,
                        resolvedLegendOffset,
                        resolvedLegendUnit,
                    )
                }`,
            ] as const;
        }),
    );
    const formattedCurrentTrafficLightValue = resolvedLegendTrafficLightValue === null
        ? null
        : formatTrafficLightLegendThreshold(
            resolvedLegendTrafficLightValue,
            locale,
            resolvedLegendConversion,
            resolvedLegendFractionDigits,
            resolvedLegendOffset,
            resolvedLegendUnit,
        );
    const resolvedValueLabel = resolveTimeSeriesValueLabel(value, valueLabels);
    const resolvedMetaText = metaDate ? formatKpiDateTime(metaDate, locale) : metaText;
    const formattedValue =
        displayValue ?? resolvedValueLabel ?? (
            transformedValue === null ? null : formatTimeSeriesNumericValue(
                transformedValue,
                locale,
                fractionDigits,
                trimFractionDigitsForIntegers,
            )
        );
    const trafficLightStyle: TimeSeriesValueCardStyle | undefined = trafficLight?.colors
        ? {
            ...(trafficLight.colors.success
                ? { '--time-series-kpi-success': trafficLight.colors.success }
                : {}),
            ...(trafficLight.colors.warning
                ? { '--time-series-kpi-warning': trafficLight.colors.warning }
                : {}),
            ...(trafficLight.colors.danger
                ? { '--time-series-kpi-danger': trafficLight.colors.danger }
                : {}),
            ...(trafficLight.colors.implausible
                ? { '--time-series-kpi-implausible': trafficLight.colors.implausible }
                : {}),
            ...(trafficLightSeverity && trafficLight.colors[trafficLightSeverity]
                ? {
                    '--status-background': `linear-gradient(
                        180deg,
                        color-mix(in srgb, ${trafficLight.colors[trafficLightSeverity]} 18%, var(--color-base-100)),
                        color-mix(in srgb, ${trafficLight.colors[trafficLightSeverity]} 24%, var(--color-base-200))
                    )`,
                }
                : {}),
        }
        : undefined;
    const trafficLightClassName = trafficLightSeverity
        ? ` ${getStatusClassName(getStatusSeverityFromTrafficLight(trafficLightSeverity))}`
        : '';
    const legendClassName = showLegend && legendSeverities.length > 0
        ? ' time-series-kpi--with-legend'
        : '';
    const interactionStateClassName = link || modal ? ' interactive-surface time-series-kpi--interactive' : '';
    const className = `time-series-kpi${trafficLightClassName}${legendClassName}${interactionStateClassName}`;
    const interactiveClassName =
        'cursor-pointer no-underline transition-all duration-150 focus-visible:outline-none';
    const modalTitle = modal?.title ?? resolvedTitle ?? t('Details');
    const modalButtonLabel = modal?.buttonLabel ?? t('OK');
    function renderContent(content: ReactNode): ReactNode {
        if (link) {
            return (
                <a
                    className={`${className} ${interactiveClassName}`}
                    href={link}
                    style={trafficLightStyle}
                >
                    {content}
                </a>
            );
        }

        if (modal) {
            return (
                <>
                    <button
                        type="button"
                        className={`${className} ${interactiveClassName} appearance-none border-0 bg-transparent`}
                        style={trafficLightStyle}
                        onClick={() => {
                            setIsCardModalOpen(true);
                        }}
                    >
                        {content}
                    </button>
                    {isCardModalOpen ? (
                        <div
                            className="modal modal-open"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="time-series-value-card-modal-title"
                        >
                            <div className="modal-box max-w-lg">
                                <div className="mb-4 flex items-start justify-between gap-4">
                                    <h2 id="time-series-value-card-modal-title">
                                        {modalTitle}
                                    </h2>
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm btn-circle"
                                        aria-label={t('Close')}
                                        onClick={() => {
                                            setIsCardModalOpen(false);
                                        }}
                                    >
                                        ✕
                                    </button>
                                </div>
                                <p>{modal.message}</p>
                                <div className="modal-action">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => {
                                            setIsCardModalOpen(false);
                                        }}
                                    >
                                        {modalButtonLabel}
                                    </button>
                                </div>
                            </div>
                            <div
                                className="modal-backdrop bg-black/40"
                                onClick={() => {
                                    setIsCardModalOpen(false);
                                }}
                            />
                        </div>
                    ) : null}
                </>
            );
        }

        return (
            <div className={className} style={trafficLightStyle}>
                {content}
            </div>
        );
    }

    if (isLoading) {
        return renderContent(<AppLoader label={loadingLabel ?? t('Loading...')} />);
    }

    if (errorMessage) {
        return renderContent(
            <>
                {resolvedTitle ? <div className="time-series-kpi__title">{resolvedTitle}</div> : null}
                <div className="time-series-kpi__message">
                    <SensorRequestMessage message={errorMessage} />
                </div>
            </>,
        );
    }

    return renderContent(
        <>
            {resolvedTitle ? <div className="time-series-kpi__title">{resolvedTitle}</div> : null}
            <div className="time-series-kpi__value">
                {formattedValue ?? '-'}
                {resolvedUnit && !resolvedValueLabel ? (
                    <span className="time-series-kpi__unit">{resolvedUnit}</span>
                ) : null}
            </div>
            {resolvedMetaText ? <div className="time-series-kpi__meta">{resolvedMetaText}</div> : null}
            {info ? (
                <button
                    type="button"
                    className="time-series-kpi__info-trigger"
                    aria-label={t('Open info')}
                    title={info}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setIsInfoModalOpen(true);
                    }}
                    onMouseDown={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Info className="h-4 w-4" aria-hidden="true" />
                </button>
            ) : null}
            {showLegend && legendSeverities.length > 0 ? (
                <div
                    className="time-series-kpi__legend"
                    role="button"
                    tabIndex={0}
                    aria-label={t('Open traffic light legend')}
                    title={t('Open traffic light legend')}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setIsLegendModalOpen(true);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            event.stopPropagation();
                            setIsLegendModalOpen(true);
                        }
                    }}
                >
                    {legendSeverities.map((severity) => (
                        <div
                            key={severity}
                            className={`time-series-kpi__legend-item${
                                trafficLightSeverity === severity
                                    ? ' time-series-kpi__legend-item--active'
                                    : ''
                            }`}
                            aria-label={legendTitles.get(severity) ?? severity}
                            title={legendTitles.get(severity) ?? severity}
                        >
                            <span
                                className={`time-series-kpi__legend-swatch time-series-kpi__legend-swatch--${severity}`}
                                aria-hidden="true"
                            />
                        </div>
                    ))}
                </div>
            ) : null}
            {isInfoModalOpen ? (
                <div
                    className="modal modal-open"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="time-series-value-card-info-modal-title"
                >
                    <div className="modal-box max-w-xl">
                        <div className="mb-4 flex items-start justify-between gap-4">
                            <div className="flex min-w-0 flex-1 flex-col gap-1 pl-4 text-left">
                                <h2 id="time-series-value-card-info-modal-title">
                                    {t('Information')}
                                </h2>
                                {title ? <h3>{title}</h3> : null}
                            </div>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-circle"
                                aria-label={t('Close')}
                                onClick={() => {
                                    setIsInfoModalOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="px-4 text-left">
                            <p>{info}</p>
                        </div>
                        <div className="modal-action">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                    setIsInfoModalOpen(false);
                                }}
                            >
                                {t('Close')}
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop bg-black/40"
                        onClick={() => {
                            setIsInfoModalOpen(false);
                        }}
                    />
                </div>
            ) : null}
            {isLegendModalOpen ? (
                <div
                    className="modal modal-open"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="time-series-value-card-legend-modal-title"
                        >
                    <div className={showTechnicalDetails && legendCode
                        ? 'modal-box max-w-7xl'
                        : 'modal-box max-w-2xl'}>
                        <div className="mb-4 flex items-start justify-between gap-4">
                            <div className="flex min-w-0 flex-1 flex-col gap-1 pl-4 text-left">
                                <h2 id="time-series-value-card-legend-modal-title">
                                    {t('Traffic light legend')}
                                </h2>
                                {title ? <h3>{title}</h3> : null}
                            </div>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-circle"
                                aria-label={t('Close')}
                                onClick={() => {
                                    setIsLegendModalOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            {formattedCurrentTrafficLightValue || legendSensor ? (
                                <div className="mb-4 flex flex-col gap-2 px-4 text-left text-sm font-medium text-base-content/80">
                                    {formattedCurrentTrafficLightValue ? (
                                        <p>
                                            <span className="text-base-content/60">{t('Current value')}:</span>{' '}
                                            <span className="text-base-content">{formattedCurrentTrafficLightValue}</span>
                                        </p>
                                    ) : null}
                                    {legendSensor ? (
                                        <p>
                                            <span className="text-base-content/60">{t('Current sensor')}:</span>{' '}
                                            <code className="break-all text-xs text-base-content">{legendSensor}</code>
                                        </p>
                                    ) : null}
                                    {legendDisplaySensor ? (
                                        <p className="text-base-content/70">
                                            <span>{t('Value display sensor')}:</span>{' '}
                                            <code className="break-all text-xs text-base-content">{legendDisplaySensor}</code>
                                        </p>
                                    ) : null}
                                </div>
                            ) : null}
                            <table className="table table-zebra">
                                <thead>
                                    <tr>
                                        <th>{t('State')}</th>
                                        <th>{t('Rule')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {legendSeverities.map((severity) => (
                                        <tr
                                            key={severity}
                                            className={legendTrafficLightSeverity === severity ? 'font-semibold' : undefined}
                                        >
                                            <td>
                                                <div className="inline-flex items-center gap-3">
                                                    <span className={`time-series-kpi__legend-item${
                                                        legendTrafficLightSeverity === severity
                                                            ? ' time-series-kpi__legend-item--active'
                                                            : ''
                                                    }`}>
                                                        <span
                                                            className={`time-series-kpi__legend-swatch time-series-kpi__legend-swatch--${severity}`}
                                                            aria-hidden="true"
                                                        />
                                                    </span>
                                                    <span>{getTrafficLightSeverityLabel(severity, resolvedLegendTrafficLight, t)}</span>
                                                </div>
                                            </td>
                                            <td>{legendTitles.get(severity) ?? getTrafficLightSeverityLabel(severity, resolvedLegendTrafficLight, t)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {showTechnicalDetails && legendNote ? (
                                <p className="mb-0 mt-4 px-4 text-left text-sm text-base-content/70">
                                    {legendNote}
                                </p>
                            ) : null}
                            {showTechnicalDetails && legendCode ? (
                                <CodeBlock
                                    code={legendCode}
                                    language="typescript"
                                    theme={codeBlockTheme}
                                    wrapperClassName="mx-4 mb-0 mt-3"
                                />
                            ) : showTechnicalDetails && legendCodeMissingMessage ? (
                                <p className="mb-0 mt-3 px-4 text-left text-sm text-base-content/70">
                                    {legendCodeMissingMessage}
                                </p>
                            ) : null}
                        </div>
                        <div className="modal-action">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                    setIsLegendModalOpen(false);
                                }}
                            >
                                {t('Close')}
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop bg-black/40"
                        onClick={() => {
                            setIsLegendModalOpen(false);
                        }}
                    />
                </div>
            ) : null}
        </>,
    );
}

export default TimeSeriesValueCard;
